import {
  Box,
  Flex,
  HStack,
  Icon,
  Input,
  Menu,
  MenuButton,
  MenuList,
  Select,
  Spinner,
  Stack,
  Tab,
  TabList,
  TabPanels,
  Tabs,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { UpdateRequestState } from '@components/bedrock/pages/agents/openbook/AddCarrierForm/enums';
import SharedButton from '@components/crm/lead/shared/Button';
import IconButton from '@components/crm/lead/shared/IconButton';
import SpinnerPage from '@components/icons/SpinnerPage';
import Button from '@components/interactive/Button';
import DefaultLayout from '@components/layout/layout/DefaultLayout';
import Body2 from '@components/typography/Body2';
import Header from '@components/typography/Header';
import {
  faChevronLeft,
  faChevronRight,
  faEllipsisVertical,
  faPlus,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBook, getBookCredentials, getBookMetrics, postRefreshCarrier } from '@lib/requests';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDate } from '@utils/formatDate';
import { OpenbookCarriers, OpenbookCarriersValues } from '@utils/index';
import { useEffect, useMemo, useState } from 'react';
import AddCarrierModal from './AddCarrierModal';
import ApplicationsFilters from './ApplicationsFilters';
import ApplicationsStatusFilter from './ApplicationsStatusFilter';
import BookFilters from './BookFilters';
import BookStatusFilter from './BookStatusFilter';
import BookTable from './BookTable';

export enum OpenbookStatusEnum {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  FUTURE_ACTIVE = 'FUTURE_ACTIVE',
  PENDING = 'PENDING',
  FAILED = 'FAILED',
}

export const OpenbookStatus = {
  [OpenbookStatusEnum.ACTIVE]: {
    key: OpenbookStatusEnum.ACTIVE,
    label: 'Active',
    colorScheme: 'green',
  },
  [OpenbookStatusEnum.INACTIVE]: {
    key: OpenbookStatusEnum.INACTIVE,
    label: 'Inactive',
    colorScheme: 'red',
  },
  [OpenbookStatusEnum.FUTURE_ACTIVE]: {
    key: OpenbookStatusEnum.FUTURE_ACTIVE,
    label: 'Future Active',
    colorScheme: 'blue',
  },
  [OpenbookStatusEnum.PENDING]: {
    key: OpenbookStatusEnum.PENDING,
    label: 'Pending',
    colorScheme: 'orange',
  },
  [OpenbookStatusEnum.FAILED]: {
    key: OpenbookStatusEnum.FAILED,
    label: 'Failed',
    colorScheme: 'red',
  },
};

export const OpenbookStatusValues = Object.values(OpenbookStatus);

const AgentsOpenbook = () => {
  // Resources
  const toast = useToast();
  const queryClient = useQueryClient();

  // State
  const [selectedStatus, setSelectedStatus] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const [carrier, setCarrier] = useState('');
  const [take, setTake] = useState(15);
  const [page, setPage] = useState('1');
  const [orderBy, setOrderBy] = useState<any>({ firstName: 'desc' });
  const [searchString, setSearchString] = useState('');

  const [initialCarrier, setInitialCarrier] = useState('');
  const [carriersUpdating, setCarriersUpdating] = useState<string[]>([]);

  const sortColumn = Object.keys(orderBy)[0];
  const sortDirection = orderBy[sortColumn];
  const isApplications = tabIndex === 0;

  // Query – Get Book Metrics
  const { data: metrics } = useQuery({
    queryKey: ['getBookMetrics', carrier],
    queryFn: async () => getBookMetrics({ carrier: carrier || null }),
  });

  // Query – Get Book Summary
  const { data: credentialsData } = useQuery({
    queryKey: ['getBookCredentials'],
    queryFn: getBookCredentials,
    refetchInterval: 5000,
  });

  // Mutation – Refresh Carrier
  const { mutateAsync: refreshCarrier } = useMutation({
    mutationKey: ['postRefreshCarrier'],
    mutationFn: postRefreshCarrier,
  });

  // Count updating carriers as refreshing if:
  // 1. The carrier is updating
  // 2. The carrier is not basic auth
  // 3. The carrier is not in a post-auth state
  const carriersRefreshing = useMemo(() => {
    return carriersUpdating.filter((carrier) => {
      const updateRequestStatus = credentialsData.updateRequests[carrier];
      return [
        UpdateRequestState.Queued,
        UpdateRequestState.PreAuth,
        UpdateRequestState.NeedsToken,
        UpdateRequestState.BadToken,
        UpdateRequestState.PostAuth,
      ].includes(updateRequestStatus);
    });
  }, [carriersUpdating, credentialsData]);

  // Handlers
  const handleRefreshCarrier = async ({ carrier }) => {
    if (carrier.auth === 'basic') {
      setCarriersUpdating([...carriersUpdating, carrier.key]);
      await refreshCarrier({ carrier: carrier.key });
      queryClient.invalidateQueries(['getBookSummary']);
    } else {
      setInitialCarrier(carrier.key);
      setShowModal(true);
    }
  };

  const showCarrierStatusToast = ({ carrier }) => {
    const id = `${carrier}-status`;

    if (toast.isActive(id) || !credentialsData) return;

    const row = credentialsData.credentials.find((row) => row.carrier === carrier);

    toast({
      id,
      title: `Refreshing your ${OpenbookCarriers[row.carrier].name} book`,
      colorScheme: 'primary',
      status: 'loading',
      duration: null,
      position: 'top',
    });
  };

  const getBookParams = {
    orderBy,
    take,
    page,
    carrier: carrier || null,
    status: selectedStatus || null,
    view: isApplications ? 'applications' : 'book',
    searchString,
  };

  // Query – Get Book Table
  const { data: bookData } = useQuery({
    queryKey: ['getBook', getBookParams],
    queryFn: async () => getBook(getBookParams),
    enabled: page !== '' && parseInt(page) > 0,
  });

  // Listen for update requests and create toast when complete
  useEffect(() => {
    if (!credentialsData) return;
    // Reset carriersUpdating state to carriers in updateRequests
    setCarriersUpdating(credentialsData.updateRequests.carriers);
  }, [credentialsData]);

  useEffect(() => {
    // Iterate through OpenbookCarriersValues
    OpenbookCarriersValues.forEach((carrier) => {
      const toastKey = `${carrier.key}-status`;
      const isActive = toast.isActive(toastKey);
      const updateRequestStatus = credentialsData?.updateRequests[carrier.key];

      // If the carrier is currently updating
      if (carriersRefreshing?.includes(carrier.key)) {
        // If the toast is not active, show it
        if (!isActive && updateRequestStatus !== UpdateRequestState.Failed) {
          showCarrierStatusToast({ carrier: carrier.key });
        }
        // If the carrier is not updating but the toast is active, close it
      } else if (isActive) {
        toast.close(toastKey);

        if (updateRequestStatus === UpdateRequestState.AccountLocked) {
          toast({
            title: `${carrier.name} account is locked`,
            position: 'top',
            status: 'error',
          });
          return;
        }
        if (updateRequestStatus === UpdateRequestState.Failed) {
          toast({
            title: `${carrier.name} update failed`,
            position: 'top',
            status: 'error',
          });
          return;
        }

        toast({
          title: `${carrier.name} update complete`,
          colorScheme: 'primary',
          position: 'top',
          status: 'success',
        });
      }
    });
  }, [credentialsData, carriersRefreshing]);

  const statusToRows = {
    [OpenbookStatusEnum.ACTIVE]: bookData?.numActive,
    [OpenbookStatusEnum.INACTIVE]: bookData?.numInactive,
    [OpenbookStatusEnum.FUTURE_ACTIVE]: bookData?.numFuture,
  };

  const numRows = selectedStatus ? statusToRows[selectedStatus] : bookData?.numTotal;
  const disableNextPage = numRows <= take * parseInt(page);

  // Loading state
  if (!credentialsData) return <SpinnerPage />;

  return (
    <DefaultLayout>
      <Stack w="100%" maxW="1450px" py={8} px={4} mx="auto" spacing={4}>
        <HStack width="100%" gap={4} justifyContent="space-between">
          <Header as="h4">Your Medicare Book</Header>
          <HStack>
            <SharedButton
              variant="primary"
              size="md"
              rightIcon={<Icon as={FontAwesomeIcon} icon={faPlus} />}
              onClick={() => {
                setInitialCarrier('');
                setShowModal(true);
                queryClient.invalidateQueries(['getBookCredentials']);
              }}
            >
              Add Carrier
            </SharedButton>
            <Box>
              <Menu>
                <Button as={MenuButton} variant="secondary" m={0}>
                  <Icon as={FontAwesomeIcon} icon={faEllipsisVertical} />
                </Button>
                <MenuList p={4} m={0}>
                  <Stack spacing={3} m={0}>
                    {credentialsData?.credentials.map((row) => {
                      const carrier = OpenbookCarriers[row.carrier];
                      return (
                        <HStack key={row.carrier} w="100%" minW="260px" justifyContent="space-between">
                          <Stack spacing={0}>
                            <Body2 bold>{carrier.name}</Body2>
                            <Body2 fontSize={14} color="gray.500">
                              Updated {formatDate(row.updatedAt)}
                            </Body2>
                          </Stack>
                          <IconButton
                            size="sm"
                            variant="secondary"
                            label="Refresh"
                            onClick={() => handleRefreshCarrier({ carrier })}
                            icon={faRefresh}
                            isLoading={carriersRefreshing.includes(row.carrier)}
                          />
                        </HStack>
                      );
                    })}
                  </Stack>
                </MenuList>
              </Menu>
            </Box>
          </HStack>
        </HStack>
        <Tabs
          index={tabIndex}
          onChange={(index) => {
            setPage('1');
            setTabIndex(index);
            setSelectedStatus('');
          }}
          colorScheme="primary"
          isLazy
        >
          <TabList alignItems="center">
            <Tab fontSize={18} fontWeight={600}>
              Applications
            </Tab>
            <Tab fontSize={18} fontWeight={600}>
              Book of Business
            </Tab>
          </TabList>
          <TabPanels>
            <Stack py={6} spacing={6}>
              {isApplications ? (
                <ApplicationsFilters
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                  metrics={metrics?.applications}
                  setPage={setPage}
                />
              ) : (
                <BookFilters
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                  bookData={bookData}
                  setPage={setPage}
                />
              )}

              {/* Filters */}
              <HStack width="100%" justifyContent="space-between">
                <HStack flex="0 1 400px" gap={2}>
                  <Select
                    size="sm"
                    minWidth="200px"
                    placeholder="Any Carrier"
                    value={carrier}
                    onChange={(e) => setCarrier(e.target.value || null)}
                  >
                    {credentialsData?.credentials.map((cred) => (
                      <option key={cred.carrier} value={cred.carrier}>
                        {OpenbookCarriers[cred.carrier].name}
                      </option>
                    ))}
                  </Select>
                  {isApplications ? (
                    <ApplicationsStatusFilter selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
                  ) : (
                    <BookStatusFilter selectedStatus={selectedStatus} setSelectedStatus={setSelectedStatus} />
                  )}
                </HStack>
                {/* Search */}
                <HStack gap={2}>
                  <Tooltip label="Search by name or email">
                    <Input
                      id="search"
                      type="text"
                      name="search"
                      size="sm"
                      value={searchString}
                      placeholder="Search"
                      onChange={(e) => setSearchString(e.target.value)}
                    />
                  </Tooltip>
                  <HStack>
                    <IconButton
                      size="sm"
                      variant="secondary"
                      label="Previous Page"
                      icon={faChevronLeft}
                      isDisabled={page === '1'}
                      onClick={() => setPage(`${parseInt(page) - 1}`)}
                    />
                    <IconButton
                      size="sm"
                      variant="secondary"
                      label="Next Page"
                      icon={faChevronRight}
                      isDisabled={disableNextPage}
                      onClick={() => setPage(`${parseInt(page) + 1}`)}
                    />
                  </HStack>
                  <Select size="sm" value={take} onChange={(e) => setTake(parseInt(e.target.value))}>
                    <option value={15}>15 rows</option>
                    <option value={50}>50 rows</option>
                    <option value={100}>100 rows</option>
                  </Select>
                </HStack>
              </HStack>
              {bookData ? (
                <BookTable
                  bookData={bookData}
                  setOrderBy={setOrderBy}
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  tabIndex={tabIndex}
                />
              ) : (
                <Flex w="100%" justifyContent="center" py={20}>
                  <Spinner />
                </Flex>
              )}
            </Stack>
          </TabPanels>
        </Tabs>
        <HStack w="100%" justifyContent="center">
          <Body2 bold>{bookData?.rows?.length} results</Body2>
        </HStack>
        <AddCarrierModal isOpen={showModal} onClose={() => setShowModal(false)} initialCarrier={initialCarrier} />
      </Stack>
    </DefaultLayout>
  );
};

export default AgentsOpenbook;
